import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Link,
  Slide,
  CircularProgress,
  FormHelperText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { MuiOtpInput } from "mui-one-time-password-input";
import { LuX } from "react-icons/lu";
import { useFormik } from "formik";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { verifyOtp, login } from "../../redux/reducers/auth/authService";
import { useTranslation } from "react-i18next";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const VerificationDialog = React.memo(({ open, onClose, setRegisterOpen }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [timer, setTimer] = useState(30);
  const [isResendActive, setIsResendActive] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState(null);
  const { t } = useTranslation();

  // Create validation schema with translations
  const validationSchema = yup.object({
    otp: yup
      .string()
      .min(6, t("verificationDialog.errorMessages.otpValid"))
      .required(t("verificationDialog.errorMessages.otpRequired")),
  });

  // Get auth state from Redux
  const { loading, error } = useSelector((state) => state.auth);

  useEffect(() => {
    let interval;
    if (timer > 0 && open) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendActive(true);
    }
    return () => clearInterval(interval);
  }, [timer, open]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Get the email from Redux state or localStorage
  const email = localStorage.getItem("registrationEmail");

  const formik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const verificationCode = values.otp;
        const resultAction = await dispatch(
          verifyOtp({
            email,
            verificationCode: parseInt(verificationCode, 10),
          })
        );

        if (verifyOtp.fulfilled.match(resultAction)) {
          const { role } = resultAction.payload.data;

          // Navigate based on user role
          if (role === "creator") {
            navigate("/creator/dashboard");
          } else if (role === "brand") {
            navigate("/brand/dashboard");
          }

          // Clean up
          onClose();
          localStorage.removeItem("registrationEmail");
        }
      } catch (error) {
        console.error("Verification error:", error);
      }
    },
  });

  const handleResend = async () => {
    if (isResendActive) {
      try {
        setResendLoading(true);
        setResendError(null);

        // Get email from localStorage
        const email = localStorage.getItem("registrationEmail");

        if (!email) {
          setResendError(t("verificationDialog.errorMessages.emailNotFound"));
          setResendLoading(false);
          return;
        }

        // Call the login/resend OTP API
        const response = await dispatch(login({ email }));

        if (response.error) {
          setResendError(
            response.error.message ||
              t("verificationDialog.errorMessages.resendFailed")
          );
        } else {
          // Reset the timer and form
          setTimer(30);
          setIsResendActive(false);
          formik.resetForm();
        }
      } catch (error) {
        setResendError(
          error.message || t("verificationDialog.errorMessages.defaultError")
        );
        console.error("Resend OTP error:", error);
      } finally {
        setResendLoading(false);
      }
    }
  };

  const handleEdit = () => {
    // Go back to the registration page
    onClose();
    setRegisterOpen(true);
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      keepMounted
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          width: "500px",
          maxWidth: "100%",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {t("verificationDialog.title")}
          </Typography>
          <IconButton onClick={onClose}>
            <LuX size={24} color="black" />
          </IconButton>
        </Box>
        <Typography variant="body1" color="text.secondary" fontWeight={400}>
          {t("verificationDialog.subtitle")} {email || "your email"}.
          <Link
            component="button"
            fontWeight={600}
            onClick={handleEdit}
            sx={{
              ml: 0.5,
              fontWeight: 600,
              textDecoration: "underline",
              textDecorationThickness: "1px",
              textUnderlineOffset: "2px",
            }}
          >
            {t("verificationDialog.editButton")}
          </Link>
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 3 }}>
          <form onSubmit={formik.handleSubmit}>
            <Typography variant="subtitle1" fontWeight={500} mb={2}>
              {t("verificationDialog.otpLabel")}
              <Typography component="span" color="error">
                *
              </Typography>
            </Typography>

            <Box maxWidth="450px">
              <MuiOtpInput
                value={formik.values.otp}
                onChange={(value) => {
                  formik.setFieldValue("otp", value);
                }}
                length={6}
                autoFocus
                gap={isMobile ? 0.5 : 1}
                TextFieldsProps={{
                  placeholder: "0",
                  type: "tel",
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 1,
                  },
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f8f8f8",
                      borderRadius: "12px",
                      border: "1.5px solid",
                      borderColor:
                        (formik.touched.otp && formik.errors.otp) || error
                          ? theme.palette.error.main
                          : theme.palette.divider,
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover": {
                        borderColor:
                          (formik.touched.otp && formik.errors.otp) || error
                            ? theme.palette.error.main
                            : theme.palette.primary.main,
                      },
                      "&.Mui-focused": {
                        borderColor:
                          (formik.touched.otp && formik.errors.otp) || error
                            ? theme.palette.error.main
                            : theme.palette.primary.main,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: "40px", sm: "45px" },
                      width: { xs: "40px", sm: "45px" },
                      padding: { xs: "4px", sm: "8px", md: "12px" },
                      textAlign: "center",
                      fontSize: { xs: "1rem", sm: "1.2rem" },
                      WebkitAppearance: "none",
                      margin: 0,
                      MozAppearance: "textfield",
                      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                        {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                    },
                  },
                }}
              />
              <Box display="flex" flexDirection="column" alignItems="center">
                {formik.touched.otp && formik.errors.otp && (
                  <FormHelperText error>{formik.errors.otp}</FormHelperText>
                )}
                {formik.values.otp && error && (
                  <FormHelperText error>
                    {error.message ||
                      t("verificationDialog.errorMessages.verificationFailed")}
                  </FormHelperText>
                )}
                {resendError && (
                  <FormHelperText error>{resendError}</FormHelperText>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Typography variant="body2" color="common.black">
                {t("verificationDialog.notReceivedText")}{" "}
                <Link
                  component="button"
                  onClick={handleResend}
                  disabled={!isResendActive || resendLoading}
                  sx={{
                    color: isResendActive
                      ? theme.palette.primary.main
                      : "text.secondary",
                    textDecoration: "none",
                    opacity: isResendActive && !resendLoading ? 1 : 0.7,
                    mb: 0.5,
                    cursor:
                      isResendActive && !resendLoading ? "pointer" : "default",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {resendLoading ? (
                    <>
                      <CircularProgress size={14} sx={{ mr: 0.5 }} />
                      {t("verificationDialog.sendingText")}
                    </>
                  ) : (
                    t("verificationDialog.resendButton")
                  )}
                </Link>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatTime(timer)}
              </Typography>
            </Box>

            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              loading={loading}
              loadingIndicator={<CircularProgress size={24} />}
              sx={{ mt: 3 }}
            >
              {t("verificationDialog.submitButton")}
            </LoadingButton>
          </form>
        </Box>
      </DialogContent>
    </Dialog>
  );
});

export default VerificationDialog;
